import { useCallback, useEffect, useRef } from 'react';
import { chatFiles, isQueuedMessage, type Message } from '@jerome2/shared';
import { ApiError } from '../api.ts';
import { createStore, useStore } from '../lib/store.ts';
import { loadMessages, removeQueuedMessage, stopChat } from '../state/chat.ts';
import { errorToast, toast } from '../state/toast.ts';

// De wachtrij van één gesprek, voor elk scherm met een invoervak (chat, agenda, regels, ontwikkelen): Stop en het
// kruisje bij een wachtend bericht halen berichten uit de wachtrij, en wat eruit komt gaat als tekst terug naar het
// vak (`insert` van ChatInput); de chat zet via `onRemoved` ook de bestanden van zo'n bericht terug in de lade. Zo
// verdwijnt er nooit iets dat Jeroen al had getypt of erbij had gezet. De teruggave staat per gesprek in
// `queueReturns` tot het vak van dat gesprek haar neemt (`takeInsert`): ook als het vak even weg was (Terug op de
// telefoon, een ander gesprek of tabblad) komt ze er één keer in, en twee teruggaven vlak na elkaar tellen op.
// Stop in de werkerskolom (ticket 30961, deel 2, `stopTurn`) doet hetzelfde als Stop in het scherm dat het gesprek toont (het laatst
// geopende, `screenStops`), zodat ook de bestanden terug in zijn lade gaan; toont geen scherm het gesprek, dan staat de tekst klaar
// voor het vak en noemt een melding de bestanden die niet terug kunnen, zoals bij een bericht van een ander apparaat.

/** De tekst in het vak terwijl Jerome bezig is: versturen kan gewoon, het bericht wacht dan op zijn beurt. */
export const QUEUE_PLACEHOLDER = 'Volgende opdracht (Jerome pakt hem op zodra hij klaar is)';
/** Dezelfde aanwijzing voor de telefoon. */
export const QUEUE_PLACEHOLDER_SHORT = 'Volgende opdracht';

/** Tekst die terug in het vak moet; elke teruggave krijgt een nieuwe `key`. */
export interface QueueReturn {
  text: string;
  key: number;
}

export interface QueueControls {
  /** Voor ChatInput: de teruggave van dit gesprek die nog niet in het vak staat. */
  insert: QueueReturn | undefined;
  /** Voor ChatInput: neemt de teruggave met deze sleutel; false als ze al genomen is of intussen is aangevuld. */
  takeInsert: (key: number) => boolean;
  /** Stopt Jerome; wat nog wachtte komt terug in het vak. */
  stop: () => void;
  /** Haalt één wachtend bericht uit de wachtrij (het kruisje); de tekst komt terug in het vak. */
  dequeue: (m: Message) => void;
}

/** Teruggaven per gesprek die nog niet in een vak staan. */
export const queueReturns = createStore<Record<string, QueueReturn>>({});
let lastKey = 0;

/** De tekst van weggehaalde berichten, één per regel. */
export function returnedText(removed: readonly Message[]): string {
  return removed.map((m) => String(m.content['text'] ?? '').trim()).filter(Boolean).join('\n');
}

/** Zet tekst klaar voor het vak van een gesprek, achter wat daar nog klaarstond. */
function giveBack(chatId: string, removed: readonly Message[]): void {
  const text = returnedText(removed);
  if (!text) return;
  const key = ++lastKey;
  queueReturns.set((s) => {
    const prev = s[chatId];
    return { ...s, [chatId]: { text: prev ? `${prev.text}\n${text}` : text, key } };
  });
}

/** Neemt de teruggave van een gesprek als die nog deze sleutel heeft; het vak zet de tekst daarna zelf neer. */
function takeReturn(chatId: string, key: number): boolean {
  if (queueReturns.get()[chatId]?.key !== key) return false;
  queueReturns.set((s) => {
    const next = { ...s };
    delete next[chatId];
    return next;
  });
  return true;
}

/** De melding bij bestanden van weggehaalde berichten die niet terug in de lade kunnen (een bericht van een ander apparaat of van
 * vóór herladen, of Stop in de werkerskolom zonder scherm met dat gesprek). */
export function filesNotBack(names: readonly string[]): void {
  if (names.length) toast(`Niet terug in de lade: ${names.join(', ')}. Voeg ze opnieuw toe als ze mee moeten.`, { tone: 'error', ms: 8000 });
}

/** Per gesprek de Stop van het scherm dat het toont (het laatst geopende). */
const screenStops = new Map<string, () => void>();

/** Stop vanuit de werkerskolom (ticket 30961, deel 2): zie de kop van dit bestand. */
export function stopTurn(chatId: string): void {
  const own = screenStops.get(chatId);
  if (own) return own();
  void stopChat(chatId)
    .then((r) => {
      giveBack(chatId, r.dequeued);
      filesNotBack(r.dequeued.flatMap((m) => chatFiles(m).map((f) => f.name)));
    })
    .catch((e) => errorToast(e, 'Niet gestopt'));
}

/** `onRemoved` krijgt de weggehaalde berichten zelf (de chat zet er de bestanden van terug in de lade). */
export function useQueue(chatId: string | null | undefined, onRemoved?: (removed: readonly Message[]) => void): QueueControls {
  const insert = useStore(queueReturns, (s) => (chatId ? s[chatId] : undefined));
  const removedRef = useRef(onRemoved);
  removedRef.current = onRemoved;
  const takeInsert = useCallback((key: number) => (chatId ? takeReturn(chatId, key) : false), [chatId]);
  const stop = useCallback(() => {
    if (!chatId) return;
    void stopChat(chatId)
      .then((r) => {
        giveBack(chatId, r.dequeued);
        removedRef.current?.(r.dequeued);
      })
      .catch((e) => errorToast(e, 'Niet gestopt'));
  }, [chatId]);
  // Stop in de werkerskolom gaat via dit scherm zolang het dit gesprek toont.
  useEffect(() => {
    if (!chatId) return;
    screenStops.set(chatId, stop);
    return () => {
      if (screenStops.get(chatId) === stop) screenStops.delete(chatId);
    };
  }, [chatId, stop]);
  /** Berichten waarvan het weghalen onderweg is: een tweede klik op hetzelfde kruisje zou van de server "staat niet meer
   * in de wachtrij" terugkrijgen en een foutmelding geven. */
  const removing = useRef(new Set<string>());
  const dequeue = useCallback(
    (m: Message) => {
      if (!chatId || removing.current.has(m.id)) return;
      removing.current.add(m.id);
      void removeQueuedMessage(chatId, m.id)
        .then((removed) => {
          giveBack(chatId, [removed]);
          removedRef.current?.([removed]);
        })
        .catch((e) => {
          errorToast(e);
          // Jerome had het al opgepakt en de melding daarover is gemist (verbinding even weg): haal de stand van de server op.
          if (e instanceof ApiError && e.status === 409) void loadMessages(chatId).catch(() => undefined);
        })
        .finally(() => removing.current.delete(m.id));
    },
    [chatId],
  );
  return { insert, takeInsert, stop, dequeue };
}

/** De laatste `n` berichten voor een smal paneel, met wat nog wacht er altijd bij: een wachtend bericht houdt zijn oude
 * volgnummer en zou anders uit beeld schuiven zodra Jerome intussen veel regels schrijft. Met `n` nul of minder alleen
 * wat wacht. */
export function recentWithQueued(messages: readonly Message[], n: number): Message[] {
  // slice(-0) geeft niet niets maar alles.
  if (n <= 0) return messages.filter(isQueuedMessage);
  const recent = messages.slice(-n);
  const waiting = messages.slice(0, -n).filter(isQueuedMessage);
  return waiting.length ? [...waiting, ...recent] : recent;
}
